import { HEADER_TEXT_LIMITS, ITEM_COLUMNS, QUANTITY_UOM_MAX_LENGTH } from './packingListFields';
import { normalizeContainerNo } from './packingListAllocation';
import { isSameParty, resolveUom } from './packingListNormalize';
import { quantityIn, secondaryQuantities } from './packingListQuantity';

const TOLERANCE = 0.005;
// 부동소수 합산 오차(0.1+0.2≠0.3)만 흡수하는 크기. 실제 문서 차이(0.0004 이상)는 그대로 드러난다.
const FLOAT_EPSILON = 1e-9;
const LINE_TEXT_LIMITS = ITEM_COLUMNS.filter((c) => c.maxLength);

const tooLong = (value, maxLength) => typeof value === 'string' && value.length > maxLength;
export const LOW_CONFIDENCE = 0.8;
/** 같은 Invoice인지 볼 때의 비교 값. 대소문자와 공백(앞뒤·가운데)은 달라도 같은 Invoice로 본다(서버 _invoice_key와 같은 규칙). */
export const invoiceKey = (value) => String(value ?? '').replace(/\s+/g, '').toLowerCase();

function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function fmt(n) {
    return Number(n.toFixed(3)).toLocaleString();
}

/** 비교용 단위 키 — 대문자, 공백·구두점 제거, 끝의 복수형 S 제거(KGS=KG, LBS=LB, CTNS=CTN). 서버 draft._unit_key와 같다. */
export function unitKey(value) {
    let key = String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (key.length >= 3 && key.endsWith('S')) key = key.slice(0, -1);
    return key;
}

const CARTON_KEYS = new Set(['CTN', 'CARTON', 'C/T', 'CT'].map(unitKey));
const isCarton = (uom) => CARTON_KEYS.has(unitKey(uom));

/** 라인마다 match(단위)가 참인 수량 한 줄. 없으면 낱개 행(0), 어느 라인에도 없으면 null(검증 불가). 서버 draft._unit_quantities와 같다. */
function unitValues(lines, match) {
    const values = lines.map((line) => {
        const quantities = line.quantities ?? [];
        const picked = quantities.filter((q) => match(unitKey(q.uom || q.uom_raw)));
        if (quantities.length === 0) return null;
        if (picked.length === 1) return toNumber(picked[0].qty);
        return picked.length === 0 ? 'none' : null;
    });
    if (values.every((v) => v === 'none' || v === null)) return values.map(() => null);
    return values.map((v) => (v === 'none' ? 0 : v));
}
const cartonValues = (lines) => unitValues(lines, (key) => CARTON_KEYS.has(key));

/** 문서 합계행(totals.quantities)에서 대표 수량·카톤이 아닌 단위(팔레트 등). 서버 draft._extra_unit_rows와 같은 선택이다. */
export function extraTotalUnits(header, totals) {
    const primary = toNumber(header?.total_qty) !== null ? unitKey(header.qty_uom) : null;
    const seen = new Set();
    return (totals?.quantities ?? []).flatMap((q) => {
        const key = unitKey(q.uom || q.uom_raw);
        const qty = toNumber(q.qty);
        if (!key || qty === null || seen.has(key) || key === primary || CARTON_KEYS.has(key)) return [];
        seen.add(key);
        return [{ unit: key, qty, label: q.uom_raw || q.uom || key }];
    });
}

/** 문서 합계행의 한 단위 수량을 고친 totals. 검수자가 파서가 잘못 읽은 팔레트 수 같은 보조 합계를 바로잡을 때 쓴다. */
export function setTotalsUnitQty(totals, unit, qty) {
    const quantities = (totals?.quantities ?? []).map((q) => (unitKey(q.uom || q.uom_raw) === unit ? { ...q, qty } : q));
    return { ...(totals ?? {}), quantities };
}

/** 검수 화면이 다시 계산하지 않는 파서 경고. 문서 단위(여러 PL·PL 아님)와 후보가 둘이던 칸 수를 알린다. 원본(parsed_payload)에서 읽어 수정 후에도 낡지 않는다. */
export function parserNotices(parsedPayload) {
    const warnings = Array.isArray(parsedPayload?.warnings) ? parsedPayload.warnings : [];
    // 여러 PL·PL 아님 같은 문서 단위 판정은 검증 경고(DOC_TYPE_OTHER·MULTIPLE_PL)로 올라가므로 여기서는 후보가 둘이던 칸만 알린다.
    const notices = [];
    const ambiguous = warnings.filter((w) => w.code === 'AMBIGUOUS_VALUE').length;
    if (ambiguous > 0) notices.push({ code: 'AMBIGUOUS_VALUE', message: `${ambiguous} value(s) had more than one candidate in the document. Hover the marked cells to see the value that was not used.` });
    return notices;
}

/**
 * 문서 합계행과 라인 합계를 문서 단위 그대로 대조한다. 환산은 하지 않는다. 서버(draft.compare_totals)와 같은 규칙이다.
 * 수량·카톤은 완전 일치, 중량·부피는 반올림 관행을 감안해 0.5%까지 허용한다(절대 오차 없음).
 * 판정: ok · mismatch · unit_mismatch(라인 단위가 문서 단위와 다르거나 라인끼리 섞임) · skipped(문서 합계가 없거나 값이 빈 라인이 있어 검증 불가).
 */
export function compareTotals(header, lines, totals = null) {
    const rows = [
        { key: 'qty', label: 'Quantity', headerField: 'total_qty', doc: toNumber(header.total_qty), unit: header.qty_uom, values: lines.map((l) => toNumber(l.qty)), units: lines.map((l) => l.qty_uom), tolerance: 0 },
        { key: 'carton', label: 'Carton count', headerField: 'total_carton_qty', doc: toNumber(header.total_carton_qty), unit: null, values: cartonValues(lines), units: [], tolerance: 0 },
        { key: 'net_weight', label: 'Net Weight', headerField: 'total_net_weight', doc: toNumber(header.total_net_weight), unit: header.weight_uom, values: lines.map((l) => toNumber(l.net_weight)), units: lines.map((l) => l.weight_uom), tolerance: TOLERANCE },
        { key: 'gross_weight', label: 'Gross Weight', headerField: 'total_gross_weight', doc: toNumber(header.total_gross_weight), unit: header.weight_uom, values: lines.map((l) => toNumber(l.gross_weight)), units: lines.map((l) => l.weight_uom), tolerance: TOLERANCE },
        { key: 'volume', label: 'Volume', headerField: 'total_volume', doc: toNumber(header.total_volume), unit: header.volume_uom, values: lines.map((l) => toNumber(l.volume)), units: lines.map((l) => l.volume_uom), tolerance: TOLERANCE },
        // 합계행의 나머지 단위(팔레트 등). 헤더 칸이 없어 totals.quantities를 직접 고친다(field = totals.<단위>).
        ...extraTotalUnits(header, totals).map(({ unit, qty, label }) => ({ key: `unit:${unit}`, label: `${label} total`, headerField: null, totalsUnit: unit, field: `totals.${unit}`, doc: qty, unit, values: unitValues(lines, (key) => key === unit), units: [], tolerance: 0 })),
    ];
    return rows.map(({ values, units, ...row }) => {
        const unitSet = new Set(units.map(unitKey).filter(Boolean));
        const mixed = unitSet.size > 1;
        if (row.doc === null) return { ...row, line: null, diff: null, verdict: 'skipped', verdictLabel: 'No document total' };
        if (lines.length === 0) return { ...row, line: null, diff: null, verdict: 'skipped', verdictLabel: 'No lines' };
        const missing = values.filter((v) => v === null).length;
        if (missing > 0) return { ...row, line: null, diff: null, verdict: 'skipped', verdictLabel: `${missing} line(s) without a value` };
        if (mixed) return { ...row, line: null, diff: null, verdict: 'unit_mismatch', mixed: true, verdictLabel: `Mixed UOM (${[...unitSet].join(', ')})` };
        const lineUnit = [...unitSet][0] ?? null;
        if (row.key !== 'carton' && !row.totalsUnit && lineUnit && row.unit && unitKey(row.unit) !== lineUnit) {
            return { ...row, line: null, diff: null, verdict: 'unit_mismatch', verdictLabel: `UOM mismatch (${lineUnit} vs ${row.unit})` };
        }
        const line = values.reduce((acc, v) => acc + v, 0);
        // 판정은 원래 차이로 한다(서버는 Decimal로 정확히 비교). 소수 셋째 자리 반올림은 표시용 diff에만 쓴다.
        const rawDiff = line - row.doc;
        const diff = Number(rawDiff.toFixed(3));
        const exact = Math.abs(rawDiff) < FLOAT_EPSILON;
        if (row.tolerance === 0) {
            return exact ? { ...row, line, diff, verdict: 'ok', verdictLabel: 'Exact match' } : { ...row, line, diff, verdict: 'mismatch', verdictLabel: 'Mismatch' };
        }
        // 문서 합계가 0이면 허용 오차도 0이다 — 라인 합이 0이 아니면 불일치.
        const pct = row.doc === 0 ? (exact ? 0 : Infinity) : Math.abs(rawDiff) / Math.abs(row.doc);
        if (pct <= row.tolerance) return { ...row, line, diff, verdict: 'ok', verdictLabel: exact ? 'Exact match' : `Within tolerance (${(pct * 100).toFixed(2)}%)` };
        return { ...row, line, diff, verdict: 'mismatch', verdictLabel: Number.isFinite(pct) ? `Over 0.5% tolerance (${(pct * 100).toFixed(2)}%)` : 'Mismatch (document total is 0)' };
    });
}

/**
 * 확정 전에 실무자가 확인해야 할 항목을 모은다.
 * 오류는 확정을 막고(품명·수량 누락, B/L에 없는 컨테이너 번호), 경고는 확인 후 진행할 수 있다. 항목마다 이동할 셀 좌표를 함께 담는다.
 */
export function validateDraft({ header, lines, totals = null, hbl, houseShipper, houseConsignee, partyAliases, uomMaster, uomAliases, confirmedInvoiceNos, parseWarnings = [] }) {
    const issues = [];
    const push = (type, item) => issues.push({ id: `${item.rule}-${item.rowId ?? 'h'}-${item.field ?? ''}`, type, ...item });
    // 확정 때 서버가 막는 것과 같은 기준이다. 표기 차이(대소문자·공백·하이픈)는 같은 번호로 보고, B/L에 컨테이너가 없으면 대조하지 않는다.
    const blContainers = (hbl?.containers ?? []).filter((c) => String(c.cntr_number ?? '').trim());
    const blContainerList = blContainers.map((c) => c.cntr_number).join(', ');
    const isOnBl = (no) => blContainers.length === 0 || blContainers.some((c) => normalizeContainerNo(c.cntr_number) === normalizeContainerNo(no));

    // 라인이 없으면 확정할 수 없다. 파싱 실패로 빈 초안이 열렸을 때 먼저 보여준다.
    if (lines.length === 0) push('ERROR', { scope: 'header', field: 'lines', rule: 'NO_LINES', message: 'No line items', details: 'Add at least one line (NEW LINE) before confirming.' });

    lines.forEach((line) => {
        const no = line.line_no ?? '';
        if (!line.description?.trim()) push('ERROR', { scope: 'line', rowId: line.id, field: 'description', rule: 'REQ_DESCRIPTION', message: `Line ${no}: description is missing`, details: 'Description is the item key and cannot be empty.' });
        const qty = toNumber(line.qty);
        if (qty === null || qty <= 0) push('ERROR', { scope: 'line', rowId: line.id, field: 'qty', rule: 'REQ_QTY', message: `Line ${no}: quantity must be greater than 0`, details: 'Enter a positive number.' });
        // 서버는 확정 때 수량 행 전부를 본다(REQ_QTY·DUPLICATE_UOM). 보조 수량도 같은 기준으로 먼저 막아 422를 받지 않게 한다.
        const others = secondaryQuantities(line.quantities ?? []);
        if (others.some((q) => toNumber(q.qty) === null || toNumber(q.qty) <= 0)) {
            push('ERROR', { scope: 'line', rowId: line.id, field: 'quantities', rule: 'REQ_QTY', message: `Line ${no}: every quantity must be greater than 0`, details: 'Fill in or remove the empty quantity in Other Qty.' });
        }
        const uoms = (line.quantities ?? []).map((q) => String(q.uom ?? '').trim().toUpperCase());
        if (new Set(uoms).size !== uoms.length) {
            push('ERROR', { scope: 'line', rowId: line.id, field: 'quantities', rule: 'DUPLICATE_UOM', message: `Line ${no}: each quantity needs a different UOM`, details: 'Two quantities use the same unit. Merge them or change one unit.' });
        }
        if (line.qty_uom && resolveUom(line.qty_uom, uomMaster, uomAliases) === null) {
            push('WARNING', { scope: 'line', rowId: line.id, field: 'qty_uom', rule: 'UOM_UNKNOWN', message: `Line ${no}: UOM "${line.qty_uom}" is not in the master`, details: 'The original text is kept. Pick a master code or register it as an alias.' });
        }
        // 입수(카톤당 낱개)는 따로 적지 않으므로, 같은 라인의 CTN·PCS 수량이 나누어떨어지는지로 카톤 구성을 확인한다.
        const ctn = quantityIn(line, 'CTN');
        const pcs = quantityIn(line, 'PCS');
        if (ctn > 0 && pcs !== null && Math.abs(pcs / ctn - Math.round(pcs / ctn)) > 0.001) {
            push('WARNING', { scope: 'line', rowId: line.id, field: 'quantities', rule: 'PACK_MATH', message: `Line ${no}: ${pcs} PCS does not divide evenly into ${ctn} CTN`, details: `That is ${fmt(pcs / ctn)} PCS per carton. Check the carton count or a loose quantity.` });
        }
        if (line.expiry_date && hbl?.eta && line.expiry_date <= hbl.eta) {
            push('WARNING', { scope: 'line', rowId: line.id, field: 'expiry_date', rule: 'EXPIRY_BEFORE_ETA', message: `Line ${no}: expiry date (${line.expiry_date}) is on or before ETA (${hbl.eta})`, details: 'The goods may expire before arrival. Re-check LOT and expiry.' });
        }
        if (line.cntr_hint?.trim() && !isOnBl(line.cntr_hint)) {
            push('ERROR', { scope: 'line', rowId: line.id, field: 'cntr_hint', rule: 'CNTR_MISMATCH', value: line.cntr_hint.trim(), message: `Line ${no}: container ${line.cntr_hint} on the PL is not on the B/L (${blContainerList})`, details: 'Fix or clear the container number to confirm.' });
        }
        // 입력 칸은 길이를 막지만 파싱으로 들어온 값은 길 수 있다. 확정 때 저장이 실패하므로 먼저 막는다.
        LINE_TEXT_LIMITS.filter((c) => tooLong(line[c.field], c.maxLength)).forEach((c) => {
            push('ERROR', { scope: 'line', rowId: line.id, field: c.field, rule: 'TOO_LONG', message: `Line ${no}: ${c.headerName.replace(' *', '')} is longer than ${c.maxLength} characters`, details: 'Shorten the value before confirming.' });
        });
        if ((line.quantities ?? []).some((q) => tooLong(q.uom, QUANTITY_UOM_MAX_LENGTH))) {
            push('ERROR', { scope: 'line', rowId: line.id, field: 'quantities', rule: 'TOO_LONG', message: `Line ${no}: a quantity UOM is longer than ${QUANTITY_UOM_MAX_LENGTH} characters`, details: 'Shorten the unit in Other Qty before confirming.' });
        }
        Object.entries(line.field_confidence ?? {}).forEach(([field, conf]) => {
            if (typeof conf === 'number' && conf < LOW_CONFIDENCE && !(line.edited_fields ?? []).includes(field)) {
                push('WARNING', { scope: 'line', rowId: line.id, field, rule: 'LOW_CONFIDENCE', message: `Line ${no} [${field}]: extraction confidence ${(conf * 100).toFixed(0)}% is below 80%`, details: 'Compare the cell with the original document.' });
            }
        });
    });

    compareTotals(header, lines, totals).forEach((row) => {
        if (row.verdict === 'mismatch') push('ERROR', { scope: 'header', field: row.headerField ?? row.field, rule: 'TOTAL_MISMATCH', message: `${row.label}: line sum ${fmt(row.line)} ≠ document total ${fmt(row.doc)}`, details: `Difference ${fmt(row.diff)} ${row.unit ?? ''}. Fix the lines or apply the line sum to the header.` });
        if (row.verdict === 'unit_mismatch') push('WARNING', { scope: 'header', field: row.headerField ?? row.field, rule: 'TOTAL_UNIT_MISMATCH', message: `${row.label}: ${row.verdictLabel}`, details: 'No automatic conversion. Align the units before comparing.' });
    });

    // 헤더 칸의 낮은 신뢰도. 손으로 고친 칸은 묻지 않는다.
    Object.entries(header.field_confidence ?? {}).forEach(([wireField, conf]) => {
        const field = wireField === 'po_no' ? 'po_number' : wireField;
        if (typeof conf === 'number' && conf < LOW_CONFIDENCE && !(header.edited_fields ?? []).includes(field)) {
            push('WARNING', { scope: 'header', field, rule: 'LOW_CONFIDENCE', message: `Header [${field}]: extraction confidence ${(conf * 100).toFixed(0)}% is below 80%`, details: 'Compare the field with the original document.' });
        }
    });
    // 파서가 패킹리스트로 보지 않은 문서. 담당자가 Doc Type을 바꾸면 확인한 것으로 보고 경고를 거둔다.
    if (header.doc_type === 'OTHER' && parseWarnings.some((w) => w?.code === 'DOC_TYPE_OTHER')) {
        push('WARNING', { scope: 'header', field: 'doc_type', rule: 'DOC_TYPE_OTHER', message: 'This file does not look like a packing list', details: parseWarnings.find((w) => w?.code === 'DOC_TYPE_OTHER')?.message || 'Check the extracted lines before confirming.' });
    }
    // 한 파일에 PL이 여럿이라 첫 번째만 읽은 문서. 나머지는 파일을 나눠 다시 올려야 하므로 확정 전까지 계속 알린다.
    const multiplePl = parseWarnings.find((w) => w?.code === 'MULTIPLE_PL');
    if (multiplePl) {
        push('WARNING', { scope: 'header', field: 'invoice_no', rule: 'MULTIPLE_PL', message: 'This file contains more than one packing list; only the first was read', details: multiplePl.message || 'Upload the other packing lists as separate files.' });
    }

    HEADER_TEXT_LIMITS.filter((l) => tooLong(header[l.field], l.maxLength)).forEach((l) => {
        push('ERROR', { scope: 'header', field: l.field, rule: 'TOO_LONG', message: `${l.label} is longer than ${l.maxLength} characters`, details: 'Shorten the value before confirming.' });
    });
    if (invoiceKey(header.invoice_no) && confirmedInvoiceNos?.some((no) => invoiceKey(no) === invoiceKey(header.invoice_no))) {
        push('WARNING', { scope: 'header', field: 'invoice_no', rule: 'DUPLICATE_INVOICE', message: `A confirmed PL with invoice no. ${header.invoice_no} already exists`, details: 'When you confirm, choose to add this as a new packing list or replace the confirmed one.' });
    }
    if (houseShipper && header.shipper_name && !isSameParty(houseShipper.name, header.shipper_name, partyAliases[houseShipper.id])) {
        push('WARNING', { scope: 'header', field: 'shipper_name', rule: 'PARTY_MISMATCH', message: `Shipper on PL ("${header.shipper_name}") differs from House ("${houseShipper.name}")`, details: 'The House value stays in effect. "Register alias" stops this warning next time.' });
    }
    if (houseConsignee && header.consignee_name && !isSameParty(houseConsignee.name, header.consignee_name, partyAliases[houseConsignee.id])) {
        push('WARNING', { scope: 'header', field: 'consignee_name', rule: 'PARTY_MISMATCH', message: `Consignee on PL ("${header.consignee_name}") differs from House ("${houseConsignee.name}")`, details: 'The House value stays in effect. "Register alias" stops this warning next time.' });
    }

    const errors = issues.filter((i) => i.type === 'ERROR');
    const warnings = issues.filter((i) => i.type === 'WARNING');
    return { issues, errors, warnings, errorCount: errors.length, warningCount: warnings.length };
}
